// Vertical landing page template (Construction, Legal, Healthcare, Marketing).
//
// One parameterized component → four thin content wrappers. Keeps copy in
// data files so it's easy to A/B and edit without re-touching markup.
//
// Sections (top to bottom):
//   1. Hero — vertical-specific badge, headline, sub, dual CTA, trust stats
//   2. Pain — the four sharpest pains for this buyer
//   3. How Karyva maps to those pains — feature-to-pain bridge
//   4. Workflow — a typical day/week storyline showing the product in motion
//   5. Why Karyva (vs alternatives) — short competitive frame
//   6. Social proof slot — quote + logos (placeholder until first customers)
//   7. FAQ — 4 vertical-specific objections
//   8. Final dual-CTA banner
import { ArrowRight, Check, type LucideIcon } from 'lucide-react';
import { Button } from '../ui/button';

const APP_URL = 'https://app.karyva.com';

export interface VerticalContent {
  vertical: string; // e.g. "construction" (used in URL and ?industry param)
  industryLabel: string; // "Construction"
  heroIcon: LucideIcon;
  heroBadge: string; // small text above the hero h1
  heroHeadline: string;
  heroSub: string;
  trustStats: Array<{ value: string; label: string }>;
  pains: Array<{ title: string; body: string }>;
  // pain → feature mapping (3-4 entries)
  bridges: Array<{
    painHook: string; // short phrase echoing the pain
    feature: string; // product capability
    body: string; // 1-2 sentences of how it solves
  }>;
  workflowTitle: string;
  workflowSteps: Array<{ when: string; what: string }>;
  whyKaryva: string[]; // 4 short bullets
  testimonial?: {
    quote: string;
    name: string;
    role: string;
  };
  logos?: string[]; // text-only logos until we have real ones
  faqs: Array<{ q: string; a: string }>;
}

interface Props {
  content: VerticalContent;
  onNavigate: (page: string) => void;
}

export function VerticalLandingPage({ content, onNavigate }: Props) {
  const {
    vertical,
    industryLabel,
    heroIcon: HeroIcon,
    heroBadge,
    heroHeadline,
    heroSub,
    trustStats,
    pains,
    bridges,
    workflowTitle,
    workflowSteps,
    whyKaryva,
    testimonial,
    logos,
    faqs,
  } = content;

  const signupUrl = `${APP_URL}/signup?industry=${vertical}`;

  return (
    <div>
      {/* ─────────────────────────── HERO ─────────────────────────── */}
      <section className="relative bg-gradient-to-br from-[#2A3B4C] via-[#3E5365] to-[#556B7C] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-[#DAA520] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#3E5365] rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#DAA520]/20 border border-[#DAA520]/40 rounded-full text-sm mb-6">
                <HeroIcon className="w-4 h-4 text-[#FFD700]" />
                <span className="text-[#FFD700]">{heroBadge}</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight">
                {heroHeadline}
              </h1>

              <p className="text-lg md:text-xl text-[#D5D9DD] mb-8 leading-relaxed">
                {heroSub}
              </p>

              <div className="flex flex-wrap gap-4">
                <a href={signupUrl}>
                  <Button className="bg-[#DAA520] hover:bg-[#B8860B] text-white px-8 py-6 text-lg">
                    Start free trial
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <Button
                  onClick={() => onNavigate('contact')}
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                >
                  Book a demo
                </Button>
              </div>

              <p className="text-sm text-[#A8B5C3] mt-4">
                14-day trial · No credit card required · Built for {industryLabel.toLowerCase()} teams
              </p>

              {/* Trust stats */}
              <div className="mt-12 pt-8 border-t border-white/20">
                <div className="grid grid-cols-3 gap-6">
                  {trustStats.map((s, i) => (
                    <div key={i}>
                      <div className="text-3xl font-bold text-[#DAA520] mb-1">{s.value}</div>
                      <div className="text-sm text-[#A8B5C3]">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="hidden md:flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-[#DAA520] opacity-20 blur-3xl rounded-full" />
                <div className="relative w-72 h-72 bg-[#3E5365]/50 border border-[#DAA520]/30 rounded-2xl flex items-center justify-center">
                  <HeroIcon className="w-32 h-32 text-[#DAA520]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────── PAIN ─────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl text-[#2A3B4C] mb-4">
              The four things that slow {industryLabel.toLowerCase()} teams down
            </h2>
            <p className="text-lg text-[#556B7C] max-w-2xl mx-auto">
              We've talked to dozens of teams. These are the patterns that come up every time.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {pains.map((p, i) => (
              <div
                key={i}
                className="bg-[#E8E9E9] rounded-lg p-6 border-l-4 border-[#DAA520]"
              >
                <h3 className="text-lg text-[#2A3B4C] mb-2">{p.title}</h3>
                <p className="text-sm text-[#556B7C] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────── BRIDGE: pain → feature ─────────────────────────── */}
      <section className="py-20 bg-[#F7F8F9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl text-[#2A3B4C] mb-4">
              How Karyva handles each one
            </h2>
            <p className="text-lg text-[#556B7C] max-w-2xl mx-auto">
              No bolt-ons. No "configure for six months." Workflows that already speak your language.
            </p>
          </div>

          <div className="space-y-6">
            {bridges.map((b, i) => (
              <div
                key={i}
                className="grid md:grid-cols-3 gap-6 items-start bg-white border border-[#C4CDD5] rounded-lg p-6"
              >
                <div className="md:col-span-1">
                  <div className="text-xs uppercase tracking-wider text-[#8B9BAB] mb-1">
                    The pain
                  </div>
                  <div className="text-[#2A3B4C]">{b.painHook}</div>
                </div>
                <div className="md:col-span-2 border-l-0 md:border-l border-[#C4CDD5] md:pl-6">
                  <div className="text-xs uppercase tracking-wider text-[#DAA520] mb-1">
                    Karyva
                  </div>
                  <div className="text-[#2A3B4C] font-medium mb-2">{b.feature}</div>
                  <p className="text-sm text-[#556B7C] leading-relaxed">{b.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────── WORKFLOW ─────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl text-[#2A3B4C] mb-4">{workflowTitle}</h2>
            <p className="text-lg text-[#556B7C] max-w-2xl mx-auto">
              A typical week, end to end — without the spreadsheet sprawl.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {workflowSteps.map((s, i) => (
              <div
                key={i}
                className="relative bg-gradient-to-br from-[#2A3B4C] to-[#3E5365] rounded-lg p-6 text-white"
              >
                <div className="absolute -top-3 -left-3 w-10 h-10 bg-[#DAA520] rounded-full flex items-center justify-center text-white font-bold">
                  {i + 1}
                </div>
                <div className="text-xs uppercase tracking-wider text-[#DAA520] mt-2 mb-2">
                  {s.when}
                </div>
                <p className="text-sm text-[#D5D9DD] leading-relaxed">{s.what}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────── WHY ─────────────────────────── */}
      <section className="py-20 bg-[#E8E9E9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl text-[#2A3B4C] mb-6">
                Why {industryLabel.toLowerCase()} teams pick Karyva
              </h2>
              <p className="text-lg text-[#556B7C] mb-8">
                Generic PM tools force you to translate your work into their language.
                Karyva already speaks {industryLabel.toLowerCase()}.
              </p>
            </div>
            <div>
              <ul className="space-y-4">
                {whyKaryva.map((w, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#DAA520] rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#2A3B4C]">{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────── SOCIAL PROOF ─────────────────────────── */}
      {testimonial && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <blockquote className="text-2xl md:text-3xl text-[#2A3B4C] leading-relaxed mb-6">
              &ldquo;{testimonial.quote}&rdquo;
            </blockquote>
            <div className="text-[#556B7C]">
              <div className="font-medium">{testimonial.name}</div>
              <div className="text-sm">{testimonial.role}</div>
            </div>
            {logos && logos.length > 0 && (
              <div className="mt-12 pt-8 border-t border-[#C4CDD5]">
                <div className="text-xs uppercase tracking-wider text-[#8B9BAB] mb-4">
                  Trusted by {industryLabel.toLowerCase()} teams
                </div>
                <div className="flex flex-wrap justify-center gap-8">
                  {logos.map((l, i) => (
                    <div
                      key={i}
                      className="text-[#8B9BAB] text-lg font-medium tracking-wider"
                    >
                      {l}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─────────────────────────── FAQ ─────────────────────────── */}
      <section className="py-20 bg-[#F7F8F9]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl text-[#2A3B4C] mb-4">
              Questions {industryLabel.toLowerCase()} buyers ask
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="bg-white border border-[#C4CDD5] rounded-lg p-6 group"
              >
                <summary className="cursor-pointer text-[#2A3B4C] font-medium list-none flex items-center justify-between">
                  <span>{f.q}</span>
                  <span className="text-[#DAA520] text-xl group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-[#556B7C] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────── FINAL CTA ─────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-[#2A3B4C] to-[#3E5365] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl mb-6">
            See Karyva run on your {industryLabel.toLowerCase()} work
          </h2>
          <p className="text-lg text-[#D5D9DD] mb-8">
            Start a 14-day trial, or book a 30-minute walkthrough with our team.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href={signupUrl}>
              <Button className="bg-[#DAA520] hover:bg-[#B8860B] text-white px-8 py-6 text-lg">
                Start free trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <Button
              onClick={() => onNavigate('contact')}
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
            >
              Book a demo
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
